import {
  compareCheckoffOperationFreshness,
  isCheckoffOperationNewerThan,
  parseCheckoffOperationId,
  type CheckoffOperationLike,
} from "./grocery-checkoff-operation";
import {
  admitCheckoffEpochRecord,
  compareCheckoffEpochs,
  isCanonicalCheckoffEpoch,
  type ServerConfirmedCheckoff,
} from "./grocery-checkoff-confirmed";

export const CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2 =
  "bb.checkoff-retirement.v2:";
export const CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3 =
  "bb.checkoff-retirement.v3:";
export const LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY =
  "bb.checkoff-retirements.v1";
export const CHECKOFF_RETIREMENTS_STORAGE_KEY =
  LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY;

export interface CheckoffRetirementRecordV2 {
  schemaVersion: 2;
  groceryListId: string;
  itemId: string;
  supersededThroughOperationId: string;
}

/** Immutable causal retirement written only by epoch-aware clients. */
export interface CheckoffRetirementRecordV3 {
  schemaVersion: 3;
  groceryListId: string;
  itemId: string;
  supersededThroughEpoch: string;
  supersededThroughOperationId: string;
}

export type EnumerableCheckoffStorage = Pick<
  Storage,
  "length" | "key" | "getItem"
> &
  Partial<Pick<Storage, "setItem" | "removeItem">>;
type RetirementStorage = Partial<EnumerableCheckoffStorage> | null | undefined;
/** Privacy-safe read evidence; never includes durable bytes or identities. */
export interface CheckoffRetirementSourceHealth {
  complete: boolean;
  malformed: boolean;
  unreadable: boolean;
}
const maxima = new Map<string, CheckoffRetirementRecordV2>();
// Observations are not maxima: a higher unconfirmed record must never erase a
// lower compatible candidate before an authoritative baseline admits both.
const v3Observed = new Map<string, Map<string, CheckoffRetirementRecordV3>>();
const listeners = new Set<() => void>();

function notifyRetirementSubscribers(): void {
  // Snapshot membership so unsubscribe during one callback affects only later
  // notifications. Subscriber failures are isolated from durable publication,
  // the in-memory maximum, and every other same-realm subscriber.
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      /* isolate subscriber faults */
    }
  }
}

function identityKey(list: string, item: string): string {
  return JSON.stringify([list, item]);
}
function validId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !value.includes(String.fromCharCode(0)) &&
    !value.includes(":") &&
    !value.includes("\\")
  );
}
function validRecord(value: unknown): value is CheckoffRetirementRecordV2 {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    Object.keys(v).length === 4 &&
    v.schemaVersion === 2 &&
    validId(v.groceryListId) &&
    validId(v.itemId) &&
    typeof v.supersededThroughOperationId === "string" &&
    parseCheckoffOperationId(v.supersededThroughOperationId) !== undefined
  );
}

function validV3Record(value: unknown): value is CheckoffRetirementRecordV3 {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    Object.keys(v).length === 5 &&
    v.schemaVersion === 3 &&
    validId(v.groceryListId) &&
    validId(v.itemId) &&
    isCanonicalCheckoffEpoch(v.supersededThroughEpoch) &&
    typeof v.supersededThroughOperationId === "string" &&
    parseCheckoffOperationId(v.supersededThroughOperationId)?.version === 3
  );
}

export function retirementRecordStorageKeyV3(
  record: CheckoffRetirementRecordV3,
): string {
  return (
    CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3 +
    [
      record.groceryListId,
      record.itemId,
      record.supersededThroughEpoch,
      record.supersededThroughOperationId,
    ]
      .map(encodeURIComponent)
      .join(":")
  );
}

export function parseCheckoffRetirementV3StorageRecord(
  key: string | null,
  raw: string | null,
): CheckoffRetirementRecordV3 | undefined {
  if (!key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3) || raw === null)
    return undefined;
  const suffix = key.slice(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3.length);
  const parts = suffix.split(":");
  if (parts.length !== 4) return undefined;
  let decoded: string[];
  try {
    decoded = parts.map(decodeURIComponent);
  } catch {
    return undefined;
  }
  if (decoded.map(encodeURIComponent).join(":") !== suffix) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!validV3Record(value)) return undefined;
  return value.groceryListId === decoded[0] &&
    value.itemId === decoded[1] &&
    value.supersededThroughEpoch === decoded[2] &&
    value.supersededThroughOperationId === decoded[3]
    ? value
    : undefined;
}

export function admitCheckoffRetirementRecord(
  record: CheckoffRetirementRecordV3,
  baseline: ServerConfirmedCheckoff | undefined,
) {
  return admitCheckoffEpochRecord(
    {
      checkoffEpoch: record.supersededThroughEpoch,
      operationId: record.supersededThroughOperationId,
    },
    baseline,
  );
}

function compareV3Retirements(
  left: CheckoffRetirementRecordV3,
  right: CheckoffRetirementRecordV3,
): number {
  const epoch = compareCheckoffEpochs(
    left.supersededThroughEpoch,
    right.supersededThroughEpoch,
  );
  if (epoch !== 0) return epoch;
  return compareCheckoffOperationFreshness(
    parseCheckoffOperationId(left.supersededThroughOperationId)!,
    parseCheckoffOperationId(right.supersededThroughOperationId)!,
  );
}

function v3RecordIdentity(record: CheckoffRetirementRecordV3): string {
  return `${record.supersededThroughEpoch}\u0000${record.supersededThroughOperationId}`;
}

function observedV3Candidates(): CheckoffRetirementRecordV3[] {
  return [...v3Observed.values()].flatMap((records) => [...records.values()]);
}

function v3MaximumForTarget(key: string): CheckoffRetirementRecordV3 | undefined {
  return [...(v3Observed.get(key)?.values() ?? [])].reduce<CheckoffRetirementRecordV3 | undefined>(
    (current, record) => !current || compareV3Retirements(record, current) > 0 ? record : current,
    undefined,
  );
}

function mergeV3(record: CheckoffRetirementRecordV3): CheckoffRetirementRecordV3 {
  const key = identityKey(record.groceryListId, record.itemId);
  const records = v3Observed.get(key) ?? new Map<string, CheckoffRetirementRecordV3>();
  records.set(v3RecordIdentity(record), record);
  v3Observed.set(key, records);
  return v3MaximumForTarget(key)!;
}

/** Loads immutable v3 maxima without treating unconfirmed durable state as authority. */
export function loadCheckoffRetirementV3Maxima(
  storage: RetirementStorage,
): CheckoffRetirementRecordV3[] {
  return loadCheckoffRetirementV3MaximaWithHealth(storage).maxima;
}

function observedV3Maxima(): CheckoffRetirementRecordV3[] {
  return [...new Map(observedV3Candidates().map((record) => [
    identityKey(record.groceryListId, record.itemId),
    v3MaximumForTarget(identityKey(record.groceryListId, record.itemId))!,
  ])).values()];
}

/** Loads v3 retirement records with fail-closed read evidence. */
export function loadCheckoffRetirementV3MaximaWithHealth(
  storage: RetirementStorage,
): { maxima: CheckoffRetirementRecordV3[]; health: CheckoffRetirementSourceHealth } {
  const health: CheckoffRetirementSourceHealth = { complete: true, malformed: false, unreadable: false };
  if (!storage) return { maxima: observedV3Maxima(), health: { complete: false, malformed: false, unreadable: true } };
  try {
    const length = storage.length ?? -1;
    const keyAt = storage.key;
    const getItem = storage.getItem;
    if (!Number.isSafeInteger(length) || length < 0 || typeof keyAt !== "function" || typeof getItem !== "function") {
      return { maxima: observedV3Maxima(), health: { complete: false, malformed: false, unreadable: true } };
    }
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      if (!key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3)) continue;
      let raw: string | null;
      try { raw = getItem.call(storage, key); } catch { health.complete = false; health.unreadable = true; continue; }
      const record = parseCheckoffRetirementV3StorageRecord(key, raw);
      if (record) mergeV3(record);
      else if (raw !== null) { health.complete = false; health.malformed = true; }
    }
  } catch {
    health.complete = false;
    health.unreadable = true;
  }
  return { maxima: observedV3Maxima(), health };
}

function loadV3RecordsForTarget(
  storage: RetirementStorage,
  groceryListId: string,
  itemId: string,
): CheckoffRetirementRecordV3[] {
  if (!storage) return [];
  try {
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const length = storage.length ?? 0;
    if (typeof keyAt !== "function" || typeof getItem !== "function") {
      return [];
    }
    const records: CheckoffRetirementRecordV3[] = [];
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      const record = key
        ? parseCheckoffRetirementV3StorageRecord(key, getItem.call(storage, key))
        : undefined;
      if (record?.groceryListId === groceryListId && record.itemId === itemId) {
        records.push(record);
      }
    }
    return records;
  } catch {
    return [];
  }
}

/**
 * Selects maxima only from records whose epoch is compatible with an explicit
 * authoritative baseline. Higher/equal-above records remain durable
 * quarantine inputs and cannot hide a compatible lower retirement.
 */
export function loadAdmittedCheckoffRetirementV3Maxima(
  storage: RetirementStorage,
  baselineFor: (groceryListId: string, itemId: string) => ServerConfirmedCheckoff | undefined,
): CheckoffRetirementRecordV3[] {
  const grouped = new Map<string, CheckoffRetirementRecordV3>();
  const admitIntoGroup = (record: CheckoffRetirementRecordV3) => {
    const admission = admitCheckoffRetirementRecord(
      record,
      baselineFor(record.groceryListId, record.itemId),
    );
    if (admission.kind === "quarantine" || admission.kind === "drop") return;
    const identity = identityKey(record.groceryListId, record.itemId);
    const current = grouped.get(identity);
    if (!current || compareV3Retirements(record, current) > 0) grouped.set(identity, record);
  };
  // Durable enumeration can be blocked after same-realm publication. Memory is
  // a candidate, never authority: it is independently baseline-admitted.
  for (const record of observedV3Candidates()) admitIntoGroup(record);
  if (!storage) return [...grouped.values()];
  try {
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const length = storage.length ?? 0;
    if (typeof keyAt !== "function" || typeof getItem !== "function") {
      return [...grouped.values()];
    }
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      const record = key
        ? parseCheckoffRetirementV3StorageRecord(key, getItem.call(storage, key))
        : undefined;
      if (!record) continue;
      admitIntoGroup(record);
    }
  } catch {
    return [...grouped.values()];
  }
  return [...grouped.values()];
}

/** Deletes only a confirmed-dominated immutable v3 record after reread. */
export function compactCheckoffRetirementV3AtBaseline(
  storage: RetirementStorage,
  groceryListId: string,
  itemId: string,
  baseline: ServerConfirmedCheckoff | undefined,
): void {
  if (!storage || !baseline) return;
  const records = loadV3RecordsForTarget(storage, groceryListId, itemId);
  const admitted = records.filter((record) => {
    const admission = admitCheckoffRetirementRecord(record, baseline);
    return admission.kind === "purge";
  });
  const maximum = admitted.reduce<CheckoffRetirementRecordV3 | undefined>(
    (current, record) =>
      !current || compareV3Retirements(record, current) > 0 ? record : current,
    undefined,
  );
  if (!maximum) return;
  try {
    const getItem = storage.getItem;
    const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof removeItem !== "function") return;
    for (const record of admitted) {
      if (record.supersededThroughEpoch === maximum.supersededThroughEpoch && record.supersededThroughOperationId === maximum.supersededThroughOperationId) continue;
      const key = retirementRecordStorageKeyV3(record);
      // Immutable keys plus exact reread prevent deleting a reentrant newer
      // record that arrived after this compaction snapshot.
      if (
        parseCheckoffRetirementV3StorageRecord(key, getItem.call(storage, key))
          ?.supersededThroughOperationId === record.supersededThroughOperationId
      ) {
        removeItem.call(storage, key);
      }
    }
  } catch {
    /* best effort; preserve every source on failure */
  }
}

/** Target-scoped authoritative purge of lower immutable v3 retirement state. */
export function purgeConfirmedCheckoffRetirementTargetState(
  storage: RetirementStorage,
  groceryListId: string,
  itemId: string,
  confirmedEpoch: string,
): number {
  if (!isCanonicalCheckoffEpoch(confirmedEpoch)) return 0;
  let removed = 0;
  const memoryKey = identityKey(groceryListId, itemId);
  const memory = v3Observed.get(memoryKey);
  if (memory) {
    for (const [recordKey, record] of memory) {
      if (compareCheckoffEpochs(record.supersededThroughEpoch, confirmedEpoch) < 0) {
        memory.delete(recordKey);
        removed += 1;
      }
    }
    if (memory.size === 0) v3Observed.delete(memoryKey);
  }
  if (!storage) return removed;
  try {
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const removeItem = storage.removeItem;
    const length = storage.length ?? 0;
    if (
      typeof keyAt !== "function" ||
      typeof getItem !== "function" ||
      typeof removeItem !== "function"
    )
      return removed;
    const keys = Array.from({ length }, (_, index) => keyAt.call(storage, index))
      .filter((key): key is string => typeof key === "string");
    for (const key of keys) {
      const record = parseCheckoffRetirementV3StorageRecord(key, getItem.call(storage, key));
      if (
        record?.groceryListId === groceryListId &&
        record.itemId === itemId &&
        compareCheckoffEpochs(record.supersededThroughEpoch, confirmedEpoch) < 0
      ) {
        removeItem.call(storage, key);
        if (getItem.call(storage, key) === null) removed += 1;
      }
    }
  } catch {
    /* no destructive claim on blocked storage */
  }
  return removed;
}

/**
 * Terminal (404) cleanup. Unlike an epoch advancement, disappearance of a
 * target invalidates every safely decoded target record, even one at a newer
 * epoch. Malformed candidates are intentionally retained because they cannot
 * be safely attributed to this target.
 */
export function purgeCheckoffRetirementTargetState(
  storage: RetirementStorage,
  groceryListId: string,
  itemId: string,
): number {
  let removed = 0;
  const memoryKey = identityKey(groceryListId, itemId);
  if (maxima.delete(memoryKey)) removed += 1;
  const observed = v3Observed.get(memoryKey);
  if (observed) removed += observed.size;
  v3Observed.delete(memoryKey);
  if (!storage) {
    if (removed > 0) notifyRetirementSubscribers();
    return removed;
  }
  try {
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    const keyAt = storage.key;
    const length = storage.length ?? 0;
    if (
      typeof getItem !== "function" ||
      typeof setItem !== "function" ||
      typeof removeItem !== "function" ||
      typeof keyAt !== "function"
    ) {
      if (removed > 0) notifyRetirementSubscribers();
      return removed;
    }
    const aggregate = getItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
    if (aggregate !== null) {
      try {
        const candidates: unknown = JSON.parse(aggregate);
        if (Array.isArray(candidates)) {
          const retained = candidates.filter(
            (candidate) =>
              !isLegacyRecord(candidate) ||
              candidate.groceryListId !== groceryListId ||
              candidate.itemId !== itemId,
          );
          if (retained.length !== candidates.length) {
            const source: LoadedLegacyCheckoffRetirementSource = {
              namespace: "v1",
              storageKey: LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY,
              raw: aggregate,
              candidates,
            };
            const journal = beginRetirementRecovery(storage, source);
            if (journal) {
              try {
                const nextRaw = retained.length === 0 ? null : JSON.stringify(retained);
                const planned = updateRetirementRecovery(storage, journal, {
                  phase: "staged",
                  expectedAfterStage: [{ key: source.storageKey, raw: nextRaw }],
                  expectedAfterDispose: [{ key: source.storageKey, raw: nextRaw }],
                  expectedFinal: [{ key: source.storageKey, raw: nextRaw }],
                });
                if (!planned || !ownsRetirementRecovery(storage, planned) || getItem.call(storage, source.storageKey) !== source.raw) {
                  rollbackRetirementRecovery(storage, source, planned ?? journal);
                } else {
                  if (nextRaw === null) removeItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
                  else setItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY, nextRaw);
                  if (getItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY) === nextRaw) {
                    if (finishRetirementRecovery(storage, planned)) {
                      removed += candidates.length - retained.length;
                    } else {
                      rollbackRetirementRecovery(storage, source, planned);
                    }
                  } else {
                    rollbackRetirementRecovery(storage, source, planned);
                  }
                }
              } catch {
                rollbackRetirementRecovery(storage, source, journal);
              }
            }
          }
        }
      } catch {
        /* preserve corrupt aggregate */
      }
    }
    const keys = Array.from({ length }, (_, index) => keyAt.call(storage, index))
      .filter((key): key is string => typeof key === "string")
      .filter((key) => key !== LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
    for (const key of keys) {
      const raw = getItem.call(storage, key);
      const v3 = parseCheckoffRetirementV3StorageRecord(key, raw);
      const v2 = parseRetirementStorageRecord(key, raw);
      const record = v3 ?? v2;
      if (
        record?.groceryListId !== groceryListId ||
        record.itemId !== itemId
      ) {
        continue;
      }
      if (raw === null) continue;
      const source: LoadedLegacyCheckoffRetirementSource = {
        namespace: "v2",
        storageKey: key,
        raw,
        candidates: [record],
      };
      const journal = beginRetirementRecovery(storage, source);
      if (!journal) continue;
      try {
        if (!ownsRetirementRecovery(storage, journal) || getItem.call(storage, key) !== raw) {
          rollbackRetirementRecovery(storage, source, journal);
          continue;
        }
        const planned = updateRetirementRecovery(storage, journal, {
          phase: "staged",
          expectedAfterStage: [{ key, raw: null }],
          expectedAfterDispose: [{ key, raw: null }],
          expectedFinal: [{ key, raw: null }],
        });
        if (!planned || !ownsRetirementRecovery(storage, planned) || getItem.call(storage, key) !== raw) {
          rollbackRetirementRecovery(storage, source, planned ?? journal);
          continue;
        }
        removeItem.call(storage, key);
        if (getItem.call(storage, key) === null) {
          if (finishRetirementRecovery(storage, planned)) removed += 1;
          else rollbackRetirementRecovery(storage, source, planned);
        } else {
          rollbackRetirementRecovery(storage, source, planned);
        }
      } catch {
        rollbackRetirementRecovery(storage, source, journal);
      }
    }
  } catch {
    /* individual verified removals above remain meaningful */
  }
  if (removed > 0) notifyRetirementSubscribers();
  return removed;
}

/**
 * Exact readback makes promotion receiver-safe. It does not compact or merge
 * v2 sources: durable records remain non-authoritative until Task 7 admits
 * them against a server-confirmed baseline.
 */
export function publishCheckoffRetirementV3(
  storage: RetirementStorage,
  record: CheckoffRetirementRecordV3,
): { durable: boolean } {
  if (!validV3Record(record)) return { durable: false };
  let durable = false;
  try {
    const setItem = storage?.setItem;
    const getItem = storage?.getItem;
    if (!storage || typeof setItem !== "function" || typeof getItem !== "function") {
      mergeV3(record);
      notifyRetirementSubscribers();
      return { durable: false };
    }
    const key = retirementRecordStorageKeyV3(record);
    const raw = JSON.stringify(record);
    setItem.call(storage, key, raw);
    durable =
      getItem.call(storage, key) === raw &&
      parseCheckoffRetirementV3StorageRecord(key, raw)
        ?.supersededThroughOperationId === record.supersededThroughOperationId;
  } catch {
    /* memory fallback below */
  }
  mergeV3(record);
  notifyRetirementSubscribers();
  return { durable };
}

/** Pure handoff for Task 7's storage-event GET pause; no event/network work. */
export function classifyCheckoffRetirementStorageEvent(
  key: string | null,
  newValue: string | null,
  baseline: ServerConfirmedCheckoff | undefined,
) {
  const record = parseCheckoffRetirementV3StorageRecord(key, newValue);
  return record
    ? { record, admission: admitCheckoffRetirementRecord(record, baseline) }
    : undefined;
}

/** Strict immutable-v3 event consumer for baseline-aware callers. */
export function mergeCheckoffRetirementV3StorageEvent(
  key: string | null,
  newValue: string | null,
): boolean {
  const record = parseCheckoffRetirementV3StorageRecord(key, newValue);
  if (!record) return false;
  mergeV3(record);
  notifyRetirementSubscribers();
  return true;
}

/**
 * Baseline-gated retirement coverage. Legacy v1/v2 records are intentionally
 * absent: they cannot delete/rebase current work until Task 7 verifies a
 * receiver-safe v3 disposition against an authoritative response.
 */
export function isCheckoffOperationRetiredAtBaseline(
  storage: RetirementStorage,
  entry: {
    groceryListId: string;
    itemId: string;
    checkoffEpoch: string;
    operationId: string;
  },
  baseline: ServerConfirmedCheckoff | undefined,
): boolean {
  if (
    !baseline ||
    !isCanonicalCheckoffEpoch(entry.checkoffEpoch) ||
    parseCheckoffOperationId(entry.operationId)?.version !== 3 ||
    !storage
  ) {
    return false;
  }
  try {
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const length = storage.length ?? 0;
    if (typeof keyAt !== "function" || typeof getItem !== "function") return false;
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      const record = key
        ? parseCheckoffRetirementV3StorageRecord(key, getItem.call(storage, key))
        : undefined;
      if (
        !record ||
        record.groceryListId !== entry.groceryListId ||
        record.itemId !== entry.itemId ||
        compareCheckoffEpochs(record.supersededThroughEpoch, baseline.checkoffEpoch) > 0 ||
        (compareCheckoffEpochs(record.supersededThroughEpoch, baseline.checkoffEpoch) === 0 &&
          baseline.checkoffOperationId !== null &&
            compareCheckoffOperationFreshness(
              parseCheckoffOperationId(record.supersededThroughOperationId)!,
              parseCheckoffOperationId(baseline.checkoffOperationId)!,
            ) > 0) ||
        compareCheckoffEpochs(entry.checkoffEpoch, record.supersededThroughEpoch) > 0
      ) {
        continue;
      }
      if (entry.checkoffEpoch !== record.supersededThroughEpoch) return true;
      if (
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(entry.operationId)!,
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
        ) <= 0
      )
        return true;
    }
  } catch {
    return false;
  }
  return false;
}

export function retirementRecordStorageKey(
  record: CheckoffRetirementRecordV2,
): string {
  return (
    CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2 +
    [record.groceryListId, record.itemId, record.supersededThroughOperationId]
      .map(encodeURIComponent)
      .join(":")
  );
}

export function parseRetirementStorageRecord(
  key: string | null,
  raw: string | null,
): CheckoffRetirementRecordV2 | undefined {
  if (!key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2) || raw === null)
    return undefined;
  const suffix = key.slice(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2.length);
  const parts = suffix.split(":");
  if (parts.length !== 3) return undefined;
  let decoded: string[];
  try {
    decoded = parts.map(decodeURIComponent);
  } catch {
    return undefined;
  }
  if (decoded.map(encodeURIComponent).join(":") !== suffix) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!validRecord(value)) return undefined;
  if (
    value.groceryListId !== decoded[0] ||
    value.itemId !== decoded[1] ||
    value.supersededThroughOperationId !== decoded[2]
  )
    return undefined;
  return value;
}

export interface LoadedLegacyCheckoffRetirementSource {
  namespace: "v1" | "v2";
  storageKey: string;
  raw: string;
  candidates: readonly unknown[];
}

const RETIREMENT_RECOVERY_STORAGE_PREFIX = "bb.checkoff-retirement-recovery.v1:";
type RetirementRawSnapshot = { key: string; raw: string | null };
type RetirementRecoveryPhase = "prepared" | "writing" | "staged" | "disposing" | "disposed" | "committed" | "rolling-back" | "rolled-back";
interface RetirementRecoveryJournal {
  version: 2; ownerId: string; lockId: string; phase: RetirementRecoveryPhase;
  snapshots: RetirementRawSnapshot[]; expectedAfterStage: RetirementRawSnapshot[];
  expectedAfterDispose: RetirementRawSnapshot[]; expectedFinal: RetirementRawSnapshot[];
}
interface RetirementRecoveryHandle { key: string; raw: string; value: RetirementRecoveryJournal }
let retirementRecoveryOwnerFactory: (() => string) | undefined;
export function setRetirementRecoveryOwnerFactoryForTests(factory: (() => string) | undefined): void { retirementRecoveryOwnerFactory = factory; }
function retirementLockId(snapshots: readonly RetirementRawSnapshot[]): string { return snapshots.map(({ key }) => key).sort().map(encodeURIComponent).join(":"); }
function nextRetirementOwnerId(): string | undefined {
  if (retirementRecoveryOwnerFactory) return retirementRecoveryOwnerFactory();
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  if (typeof globalThis.crypto?.getRandomValues === "function") return Array.from(globalThis.crypto.getRandomValues(new Uint32Array(4)), (value) => value.toString(16).padStart(8, "0")).join("");
  return undefined;
}
function retirementRecoveryKey(lockId: string, ownerId: string): string { return `${RETIREMENT_RECOVERY_STORAGE_PREFIX}${lockId}:${encodeURIComponent(ownerId)}`; }
function isRetirementRecoverySnapshotKey(key: string): boolean { return key === LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY || key.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2) || key.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V3); }
function parseRetirementRecoveryJournal(raw: string | null, key?: string): RetirementRecoveryJournal | undefined {
  if (raw === null) return undefined;
  try {
    const value: unknown = JSON.parse(raw); if (!value || typeof value !== "object") return undefined;
    const journal = value as Partial<RetirementRecoveryJournal>; const phases: RetirementRecoveryPhase[] = ["prepared", "writing", "staged", "disposing", "disposed", "committed", "rolling-back", "rolled-back"];
    const valid = (items: unknown): items is RetirementRawSnapshot[] => Array.isArray(items) && items.length > 0 && items.every((item) => !!item && typeof item === "object" && typeof (item as RetirementRawSnapshot).key === "string" && (typeof (item as RetirementRawSnapshot).raw === "string" || (item as RetirementRawSnapshot).raw === null));
    if (!(journal.version === 2 && typeof journal.ownerId === "string" && journal.ownerId.length > 0 && typeof journal.lockId === "string" && phases.includes(journal.phase as RetirementRecoveryPhase) && valid(journal.snapshots) && valid(journal.expectedAfterStage) && valid(journal.expectedAfterDispose) && valid(journal.expectedFinal))) return undefined;
    const snapshots = journal.snapshots!; const keys = snapshots.map((snapshot) => snapshot.key);
    const sameKeys = (items: RetirementRawSnapshot[]) => items.length === snapshots.length && items.every((item, index) => item.key === snapshots[index]?.key);
    if (new Set(keys).size !== keys.length || keys.some((entry) => !isRetirementRecoverySnapshotKey(entry)) || !sameKeys(journal.expectedAfterStage!) || !sameKeys(journal.expectedAfterDispose!) || !sameKeys(journal.expectedFinal!) || journal.lockId !== retirementLockId(snapshots) || (key !== undefined && key !== retirementRecoveryKey(journal.lockId, journal.ownerId))) return undefined;
    return journal as RetirementRecoveryJournal;
  } catch { return undefined; }
}
function updateRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, handle: RetirementRecoveryHandle, patch: Partial<Omit<RetirementRecoveryJournal, "version" | "ownerId" | "lockId" | "snapshots">> & Pick<RetirementRecoveryJournal, "phase">): RetirementRecoveryHandle | undefined {
  try { const getItem = storage.getItem; const setItem = storage.setItem; if (typeof getItem !== "function" || typeof setItem !== "function" || getItem.call(storage, handle.key) !== handle.raw) return undefined; const value = { ...handle.value, ...patch }; const raw = JSON.stringify(value); setItem.call(storage, handle.key, raw); return getItem.call(storage, handle.key) === raw ? { key: handle.key, raw, value } : undefined; } catch { return undefined; }
}
function retirementJournals(storage: Exclude<RetirementStorage, null | undefined>): { key: string; raw: string; value?: RetirementRecoveryJournal }[] | undefined {
  try { const keyAt = storage.key; const getItem = storage.getItem; if (typeof keyAt !== "function" || typeof getItem !== "function") return undefined; const result: { key: string; raw: string; value?: RetirementRecoveryJournal }[] = []; for (let index = 0; index < (storage.length ?? 0); index += 1) { const key = keyAt.call(storage, index); if (!key?.startsWith(RETIREMENT_RECOVERY_STORAGE_PREFIX)) continue; const raw = getItem.call(storage, key); if (raw !== null) result.push({ key, raw, value: parseRetirementRecoveryJournal(raw, key) }); } return result; } catch { return undefined; }
}
function retirementOverlaps(a: readonly RetirementRawSnapshot[], b: readonly RetirementRawSnapshot[]): boolean { return a.some((left) => b.some((right) => left.key === right.key)); }
function committedRetirementFenceMatchesCurrent(storage: Exclude<RetirementStorage, null | undefined>, journal: RetirementRecoveryJournal): boolean { try { const getItem = storage.getItem; return typeof getItem === "function" && journal.expectedFinal.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw); } catch { return false; } }
function compatibleRetirementCommittedSuccessor(storage: Exclude<RetirementStorage, null | undefined>, journal: RetirementRecoveryJournal, successors: readonly RetirementRawSnapshot[]): boolean { return retirementOverlaps(journal.snapshots, successors) && committedRetirementFenceMatchesCurrent(storage, journal) && successors.filter((snapshot) => journal.expectedFinal.some((final) => final.key === snapshot.key)).every((snapshot) => journal.expectedFinal.find((final) => final.key === snapshot.key)?.raw === snapshot.raw); }
function ownsRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, handle: RetirementRecoveryHandle): boolean {
  const all = retirementJournals(storage); if (!all || all.some((candidate) => !candidate.value)) return false;
  const overlaps = all.filter((candidate) => candidate.value && retirementOverlaps(candidate.value.snapshots, handle.value.snapshots));
  if (overlaps.some((candidate) => candidate.key !== handle.key && candidate.value!.phase !== "prepared")) return false;
  try { if (handle.value.phase !== "prepared") return storage.getItem?.call(storage, handle.key) === handle.raw; } catch { return false; }
  const prepared = overlaps.filter((candidate) => candidate.value!.phase === "prepared").sort((a, b) => a.value!.ownerId.localeCompare(b.value!.ownerId));
  try { return prepared[0]?.key === handle.key && storage.getItem?.call(storage, handle.key) === handle.raw; } catch { return false; }
}
function beginRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, source: LoadedLegacyCheckoffRetirementSource, extras: readonly RetirementRawSnapshot[] = [], expectedAfterStage?: readonly RetirementRawSnapshot[], expectedAfterDispose?: readonly RetirementRawSnapshot[], expectedFinal?: readonly RetirementRawSnapshot[]): RetirementRecoveryHandle | undefined {
  try { const getItem = storage.getItem; const setItem = storage.setItem; if (typeof getItem !== "function" || typeof setItem !== "function") return undefined; const snapshots = [{ key: source.storageKey, raw: source.raw }, ...extras].sort((a, b) => a.key.localeCompare(b.key)); const lockId = retirementLockId(snapshots); if (!snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return undefined; const existing = retirementJournals(storage); if (!existing || existing.some((candidate) => !candidate.value)) return undefined; for (const candidate of existing) if (candidate.value!.phase === "committed" && retirementOverlaps(candidate.value!.snapshots, snapshots)) { if (!clearRetirementCommittedFenceForSuccessor(storage, { key: candidate.key, raw: candidate.raw, value: candidate.value! }, snapshots)) return undefined; } if (!snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return undefined; const ownerId = nextRetirementOwnerId(); if (!ownerId) return undefined; const key = retirementRecoveryKey(lockId, ownerId); if (getItem.call(storage, key) !== null) return undefined; const reorder = (items: readonly RetirementRawSnapshot[] | undefined) => snapshots.map((snapshot) => items?.find((item) => item.key === snapshot.key) ?? snapshot); const value: RetirementRecoveryJournal = { version: 2, ownerId, lockId, phase: "prepared", snapshots, expectedAfterStage: reorder(expectedAfterStage), expectedAfterDispose: reorder(expectedAfterDispose ?? expectedAfterStage), expectedFinal: reorder(expectedFinal ?? expectedAfterDispose ?? expectedAfterStage) }; const raw = JSON.stringify(value); setItem.call(storage, key, raw); const handle = getItem.call(storage, key) === raw ? { key, raw, value } : undefined; if (!handle) return undefined; if (ownsRetirementRecovery(storage, handle)) return handle; cleanupRetirementRecovery(storage, handle); return undefined; } catch { return undefined; }
}
function cleanupRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, journal: RetirementRecoveryHandle): boolean { try { const getItem = storage.getItem; const removeItem = storage.removeItem; if (typeof getItem !== "function" || typeof removeItem !== "function" || getItem.call(storage, journal.key) !== journal.raw) return false; removeItem.call(storage, journal.key); return getItem.call(storage, journal.key) === null; } catch { return false; } }
function retireStaleRetirementRecoveryBehindCommittedFence(storage: Exclude<RetirementStorage, null | undefined>, journal: RetirementRecoveryHandle): boolean { const contenders = retirementJournals(storage); if (!contenders || contenders.some((candidate) => !candidate.value)) return false; const winner = contenders.find((candidate) => candidate.key !== journal.key && candidate.value!.phase === "committed" && retirementOverlaps(candidate.value!.snapshots, journal.value.snapshots) && committedRetirementFenceMatchesCurrent(storage, candidate.value!)); if (!winner) return false; const staleOriginalsDiffer = journal.value.snapshots.some((snapshot) => winner.value!.expectedFinal.find((final) => final.key === snapshot.key)?.raw !== snapshot.raw); return staleOriginalsDiffer && cleanupRetirementRecovery(storage, journal); }
function clearRetirementCommittedFenceForSuccessor(storage: Exclude<RetirementStorage, null | undefined>, fence: RetirementRecoveryHandle, successors: readonly RetirementRawSnapshot[]): boolean { if (!compatibleRetirementCommittedSuccessor(storage, fence.value, successors)) return false; const contenders = retirementJournals(storage); if (!contenders || contenders.some((candidate) => !candidate.value)) return false; for (const contender of contenders) { if (contender.key === fence.key || contender.value!.phase === "committed" || !retirementOverlaps(contender.value!.snapshots, fence.value.snapshots)) continue; if (!retireStaleRetirementRecoveryBehindCommittedFence(storage, { key: contender.key, raw: contender.raw, value: contender.value! })) return false; } const remaining = retirementJournals(storage); return !!remaining && remaining.every((candidate) => candidate.key === fence.key || !!candidate.value && (candidate.value.phase === "committed" || !retirementOverlaps(candidate.value.snapshots, fence.value.snapshots))) && cleanupRetirementRecovery(storage, fence); }
function rollbackRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, _source: LoadedLegacyCheckoffRetirementSource, journal: RetirementRecoveryHandle): boolean {
  if (!ownsRetirementRecovery(storage, journal)) { retireStaleRetirementRecoveryBehindCommittedFence(storage, journal); return false; }
  try { const getItem = storage.getItem; if (typeof getItem !== "function") return false; if (journal.value.phase === "prepared") return journal.value.snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw) && cleanupRetirementRecovery(storage, journal); } catch { return false; }
  let rolling = updateRetirementRecovery(storage, journal, { phase: "rolling-back" }); if (!rolling) return false;
  try { const getItem = storage.getItem; const setItem = storage.setItem; const removeItem = storage.removeItem; if (typeof getItem !== "function" || typeof setItem !== "function" || typeof removeItem !== "function") return false; let unresolved = false; for (const original of rolling.value.snapshots) { const current = getItem.call(storage, original.key); if (current === original.raw) continue; const expected = [rolling.value.expectedAfterStage, rolling.value.expectedAfterDispose, rolling.value.expectedFinal].some((state) => state.find((entry) => entry.key === original.key)?.raw === current); if (!expected) { unresolved = true; continue; } if (original.raw === null) removeItem.call(storage, original.key); else setItem.call(storage, original.key, original.raw); if (getItem.call(storage, original.key) !== original.raw) unresolved = true; } if (unresolved || !rolling.value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) return false; rolling = updateRetirementRecovery(storage, rolling, { phase: "rolled-back" }); if (!rolling) return false; cleanupRetirementRecovery(storage, rolling); return true; } catch { return false; }
}
function finishRetirementRecovery(storage: Exclude<RetirementStorage, null | undefined>, journal: RetirementRecoveryHandle): boolean { const committed = updateRetirementRecovery(storage, journal, { phase: "committed" }); return !!committed; }

/** Recovery never infers an unknown journal's mutation or touches concurrent state. */
export function resolveRetirementRecoveryJournals(storage: RetirementStorage): void {
  if (!storage) return;
  try { const keyAt = storage.key; const getItem = storage.getItem; if (typeof keyAt !== "function" || typeof getItem !== "function") return; const keys = Array.from({ length: storage.length ?? 0 }, (_, index) => keyAt.call(storage, index)).filter((key): key is string => !!key?.startsWith(RETIREMENT_RECOVERY_STORAGE_PREFIX)); for (const key of keys) { const raw = getItem.call(storage, key); const value = parseRetirementRecoveryJournal(raw, key); if (!value || raw === null) continue; const handle = { key, raw, value }; if (value.phase === "committed") continue; if (value.phase === "rolled-back") { if (value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) cleanupRetirementRecovery(storage, handle); continue; } if (retireStaleRetirementRecoveryBehindCommittedFence(storage, handle)) continue; if (value.phase === "prepared") { if (value.snapshots.every((entry) => getItem.call(storage, entry.key) === entry.raw)) cleanupRetirementRecovery(storage, handle); continue; } const known = value.snapshots.every((entry, index) => { const current = getItem.call(storage, entry.key); return current === entry.raw || current === value.expectedAfterStage[index]!.raw || current === value.expectedAfterDispose[index]!.raw || current === value.expectedFinal[index]!.raw; }); if ((value.phase === "writing" || value.phase === "staged" || value.phase === "disposing" || value.phase === "disposed" || value.phase === "rolling-back") && known) rollbackRetirementRecovery(storage, { namespace: "v2", storageKey: value.snapshots[0]!.key, raw: value.snapshots[0]!.raw ?? "", candidates: [] }, handle); } } catch { /* retained for a later load */ }
}

/**
 * Reads only legacy retirement sources. v3 is deliberately absent: it is the
 * immutable destination, not a source eligible for mutation.
 */
export function loadLegacyCheckoffRetirementSources(
  storage: RetirementStorage,
): LoadedLegacyCheckoffRetirementSource[] {
  if (!storage) return [];
  const sources: LoadedLegacyCheckoffRetirementSource[] = [];
  try {
    const getItem = storage.getItem;
    if (typeof getItem !== "function") return [];
    resolveRetirementRecoveryJournals(storage);
    const aggregate = getItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
    if (aggregate !== null) {
      try {
        const candidates: unknown = JSON.parse(aggregate);
        if (Array.isArray(candidates)) {
          sources.push({
            namespace: "v1",
            storageKey: LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY,
            raw: aggregate,
            candidates,
          });
        }
      } catch {
        /* a corrupt aggregate is preserved, never rewritten */
      }
    }
    const keyAt = storage.key;
    const length = storage.length ?? 0;
    if (typeof keyAt !== "function") return sources;
    for (let index = 0; index < length; index += 1) {
      const key = keyAt.call(storage, index);
      if (!key) continue;
      const raw = getItem.call(storage, key);
      const record = parseRetirementStorageRecord(key, raw);
      if (record && raw !== null) {
        sources.push({ namespace: "v2", storageKey: key, raw, candidates: [record] });
      }
    }
  } catch {
    /* sources already captured remain safe exact capabilities */
  }
  return sources;
}

/**
 * Promotes exactly one v1 aggregate or v2 immutable source to v3. A failed
 * source deletion intentionally leaves the source in place, even when the v3
 * promotion itself was durable, so a later receiver can retry safely.
 */
export function disposeLoadedLegacyCheckoffRetirement(
  storage: RetirementStorage,
  source: LoadedLegacyCheckoffRetirementSource | undefined,
  rawCandidate: unknown,
  supersededThroughEpoch: string,
  dispositionOperationId?: string,
): { durable: boolean } {
  if (!storage || !source || !isCanonicalCheckoffEpoch(supersededThroughEpoch)) {
    return { durable: false };
  }
  const index = source.candidates.indexOf(rawCandidate);
  const candidate =
    source.namespace === "v1"
      ? isLegacyRecord(rawCandidate)
        ? rawCandidate
        : undefined
      : validRecord(rawCandidate)
        ? rawCandidate
        : undefined;
  const disposition =
    parseCheckoffOperationId(dispositionOperationId)?.version === 3
      ? dispositionOperationId
      : parseCheckoffOperationId(candidate?.supersededThroughOperationId)?.version === 3
        ? candidate!.supersededThroughOperationId
        : undefined;
  if (index < 0 || !candidate || !disposition) {
    return { durable: false };
  }
  let journal: RetirementRecoveryHandle | undefined;
  try {
    const getItem = storage.getItem;
    const setItem = storage.setItem;
    const removeItem = storage.removeItem;
    if (typeof getItem !== "function" || typeof setItem !== "function" || typeof removeItem !== "function") {
      return { durable: false };
    }
    if (getItem.call(storage, source.storageKey) !== source.raw) {
      return { durable: false };
    }
    const v3: CheckoffRetirementRecordV3 = {
      schemaVersion: 3,
      groceryListId: candidate.groceryListId,
      itemId: candidate.itemId,
      supersededThroughEpoch,
      supersededThroughOperationId: disposition,
    };
    const v3Key = retirementRecordStorageKeyV3(v3);
    const v3Raw = JSON.stringify(v3);
    const remaining = source.namespace === "v1"
      ? source.candidates.filter((_candidate, candidateIndex) => candidateIndex !== index)
      : [];
    const sourceAfterDispose = source.namespace === "v2" || remaining.length === 0 ? null : JSON.stringify(remaining);
    const originals = [{ key: source.storageKey, raw: source.raw }, { key: v3Key, raw: getItem.call(storage, v3Key) }];
    const staged = originals.map((snapshot) => snapshot.key === v3Key ? { ...snapshot, raw: v3Raw } : snapshot);
    const disposed = staged.map((snapshot) => snapshot.key === source.storageKey ? { ...snapshot, raw: sourceAfterDispose } : snapshot);
    journal = beginRetirementRecovery(storage, source, [{ key: v3Key, raw: getItem.call(storage, v3Key) }], staged, disposed, disposed);
    if (!journal) return { durable: false };
    const fail = () => { rollbackRetirementRecovery(storage, source, journal!); return { durable: false }; };
    if (!ownsRetirementRecovery(storage, journal) || !journal.value.snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return fail();
    journal = updateRetirementRecovery(storage, journal, { phase: "writing" }) ?? journal;
    if (journal.value.phase !== "writing" || !ownsRetirementRecovery(storage, journal) || !journal.value.snapshots.every((snapshot) => getItem.call(storage, snapshot.key) === snapshot.raw)) return fail();
    setItem.call(storage, v3Key, v3Raw);
    if (getItem.call(storage, v3Key) !== v3Raw || !parseCheckoffRetirementV3StorageRecord(v3Key, v3Raw)) return fail();
    journal = updateRetirementRecovery(storage, journal, { phase: "staged" }) ?? journal;
    if (journal.value.phase !== "staged" || !ownsRetirementRecovery(storage, journal) || getItem.call(storage, source.storageKey) !== source.raw) return fail();
    journal = updateRetirementRecovery(storage, journal, { phase: "disposing" }) ?? journal;
    if (journal.value.phase !== "disposing" || !ownsRetirementRecovery(storage, journal) || getItem.call(storage, source.storageKey) !== source.raw) return fail();
    if (source.namespace === "v2") {
      removeItem.call(storage, source.storageKey);
    } else if (sourceAfterDispose === null) {
      removeItem.call(storage, source.storageKey);
    } else {
      setItem.call(storage, source.storageKey, sourceAfterDispose);
    }
    if (getItem.call(storage, source.storageKey) !== sourceAfterDispose || getItem.call(storage, v3Key) !== v3Raw) return fail();
    journal = updateRetirementRecovery(storage, journal, { phase: "disposed" }) ?? journal;
    if (journal.value.phase !== "disposed") return fail();
    if (!finishRetirementRecovery(storage, journal)) return fail();
    mergeV3(v3);
    notifyRetirementSubscribers();
    return { durable: true };
  } catch {
    if (journal) rollbackRetirementRecovery(storage, source, journal);
    return { durable: false };
  }
}

function merge(record: CheckoffRetirementRecordV2): CheckoffRetirementRecordV2 {
  const key = identityKey(record.groceryListId, record.itemId);
  const current = maxima.get(key);
  if (
    !current ||
    compareCheckoffOperationFreshness(
      parseCheckoffOperationId(record.supersededThroughOperationId)!,
      parseCheckoffOperationId(current.supersededThroughOperationId)!,
    ) > 0
  )
    maxima.set(key, record);
  return maxima.get(key)!;
}

export function loadCheckoffRetirementMaxima(
  storage: RetirementStorage,
): CheckoffRetirementRecordV2[] {
  return loadCheckoffRetirementMaximaWithHealth(storage).maxima;
}

/** Loads v2 retirement records with privacy-safe malformed/read failure evidence. */
export function loadCheckoffRetirementMaximaWithHealth(
  storage: RetirementStorage,
): { maxima: CheckoffRetirementRecordV2[]; health: CheckoffRetirementSourceHealth } {
  if (!storage) return { maxima: [...maxima.values()], health: { complete: false, malformed: false, unreadable: true } };
  const health: CheckoffRetirementSourceHealth = { complete: true, malformed: false, unreadable: false };
  const observedItems = new Map<string, { list: string; item: string }>();
  try {
    const length = storage.length ?? 0;
    const keyAt = storage.key;
    const getItem = storage.getItem;
    if (typeof keyAt !== "function" || typeof getItem !== "function") {
      return { maxima: [...maxima.values()], health: { complete: false, malformed: false, unreadable: true } };
    }
    for (let index = 0; index < length; index++) {
      const key = keyAt.call(storage, index);
      if (!key?.startsWith(CHECKOFF_RETIREMENT_STORAGE_PREFIX_V2)) continue;
      let raw: string | null;
      try { raw = getItem.call(storage, key); } catch { health.complete = false; health.unreadable = true; continue; }
      const record = parseRetirementStorageRecord(key, raw);
      if (record) {
        merge(record);
        observedItems.set(identityKey(record.groceryListId, record.itemId), {
          list: record.groceryListId,
          item: record.itemId,
        });
      } else if (raw !== null) { health.complete = false; health.malformed = true; }
    }
  } catch {
    health.complete = false;
    health.unreadable = true;
  }
  observedItems.forEach(({ list, item }) => compact(storage, list, item));
  return { maxima: [...maxima.values()], health };
}

export function retirementBoundaryFor(
  storage: RetirementStorage,
  groceryListId: string,
  itemId: string,
): string | undefined {
  return loadCheckoffRetirementMaxima(storage).find(
    (r) => r.groceryListId === groceryListId && r.itemId === itemId,
  )?.supersededThroughOperationId;
}

function compact(storage: RetirementStorage, list: string, item: string): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    const length = storage.length ?? 0;
    const keyAt = storage.key;
    const getItem = storage.getItem;
    const removeItem = storage.removeItem;
    if (
      typeof keyAt !== "function" ||
      typeof getItem !== "function" ||
      typeof removeItem !== "function"
    ) {
      return;
    }
    for (let i = 0; i < length; i++) {
      const key = keyAt.call(storage, i);
      if (key) keys.push(key);
    }
    const durableRecords = keys
      .map((key) =>
        parseRetirementStorageRecord(key, getItem.call(storage, key)),
      )
      .filter(
        (record): record is CheckoffRetirementRecordV2 =>
          record?.groceryListId === list && record.itemId === item,
      );
    const max = durableRecords.reduce<CheckoffRetirementRecordV2 | undefined>(
      (current, record) =>
        !current ||
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
          parseCheckoffOperationId(current.supersededThroughOperationId)!,
        ) > 0
          ? record
          : current,
      undefined,
    );
    if (!max) return;
    const maxFresh = parseCheckoffOperationId(
      max.supersededThroughOperationId,
    )!;
    for (const key of keys) {
      const record = parseRetirementStorageRecord(
        key,
        getItem.call(storage, key),
      );
      if (
        record &&
        record.groceryListId === list &&
        record.itemId === item &&
        record.supersededThroughOperationId !==
          max.supersededThroughOperationId &&
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
          maxFresh,
        ) < 0
      )
        removeItem.call(storage, key);
    }
  } catch {
    /* safe compaction is best effort */
  }
}

function durableMaximumFor(
  storage: RetirementStorage,
  list: string,
  item: string,
): CheckoffRetirementRecordV2 | undefined {
  if (!storage) return undefined;
  let maximum: CheckoffRetirementRecordV2 | undefined;
  try {
    const length = storage.length ?? 0;
    const keyAt = storage.key;
    const getItem = storage.getItem;
    if (typeof keyAt !== "function" || typeof getItem !== "function") {
      return undefined;
    }
    for (let i = 0; i < length; i++) {
      const key = keyAt.call(storage, i);
      const record = key
        ? parseRetirementStorageRecord(key, getItem.call(storage, key))
        : undefined;
      if (record?.groceryListId !== list || record.itemId !== item) continue;
      if (
        !maximum ||
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
          parseCheckoffOperationId(maximum.supersededThroughOperationId)!,
        ) > 0
      )
        maximum = record;
    }
  } catch {
    return undefined;
  }
  return maximum;
}

export function publishCheckoffRetirement(
  storage: RetirementStorage,
  record: CheckoffRetirementRecordV2,
): { durable: boolean } {
  if (!validRecord(record)) return { durable: false };
  let durable = false;
  try {
    const setItem = storage?.setItem;
    const getItem = storage?.getItem;
    if (
      typeof setItem === "function" &&
      typeof getItem === "function" &&
      storage
    ) {
      const key = retirementRecordStorageKey(record);
      const raw = JSON.stringify(record);
      setItem.call(storage, key, raw);
      // Web Storage can silently decline a write. Durability means this exact
      // immutable identity is observable through the same receiver, not that
      // setItem merely returned normally.
      const readback = getItem.call(storage, key);
      durable =
        readback === raw &&
        parseRetirementStorageRecord(key, readback)
          ?.supersededThroughOperationId ===
          record.supersededThroughOperationId;
    }
  } catch {
    /* memory fallback */
  }
  merge(record);
  notifyRetirementSubscribers();
  if (durable) compact(storage, record.groceryListId, record.itemId);
  return { durable };
}

export function migrateLegacyCheckoffRetirements(storage: RetirementStorage): {
  durable: boolean;
} {
  if (!storage) return { durable: false };
  let raw: string | null;
  let getItem: Storage["getItem"] | undefined;
  try {
    getItem = storage.getItem;
    if (typeof getItem !== "function") return { durable: false };
    raw = getItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
  } catch {
    return { durable: false };
  }
  // The guarded capability capture above is intentionally retained through
  // migration so removal verification uses the same native method/receiver.
  if (typeof getItem !== "function") return { durable: false };
  if (!raw) return { durable: true };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { durable: false };
  }
  if (!Array.isArray(parsed)) return { durable: false };
  const grouped = new Map<string, CheckoffRetirementRecordV2>();
  for (const candidate of parsed) {
    if (!isLegacyRecord(candidate)) continue;
    const record: CheckoffRetirementRecordV2 = {
      schemaVersion: 2,
      groceryListId: candidate.groceryListId,
      itemId: candidate.itemId,
      supersededThroughOperationId: candidate.supersededThroughOperationId,
    };
    const current = grouped.get(
      identityKey(record.groceryListId, record.itemId),
    );
    if (
      !current ||
      compareCheckoffOperationFreshness(
        parseCheckoffOperationId(record.supersededThroughOperationId)!,
        parseCheckoffOperationId(current.supersededThroughOperationId)!,
      ) > 0
    )
      grouped.set(
        identityKey(candidate.groceryListId, candidate.itemId),
        record,
      );
  }
  for (const record of grouped.values())
    if (!publishCheckoffRetirement(storage, record).durable)
      return { durable: false };
  try {
    for (const record of grouped.values()) {
      const durable = durableMaximumFor(
        storage,
        record.groceryListId,
        record.itemId,
      );
      if (
        !durable ||
        compareCheckoffOperationFreshness(
          parseCheckoffOperationId(durable.supersededThroughOperationId)!,
          parseCheckoffOperationId(record.supersededThroughOperationId)!,
        ) < 0
      )
        return { durable: false };
    }
    const removeItem = storage.removeItem;
    if (typeof removeItem !== "function") return { durable: false };
    removeItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY);
    if (getItem.call(storage, LEGACY_CHECKOFF_RETIREMENTS_STORAGE_KEY) !== null)
      return { durable: false };
    return { durable: true };
  } catch {
    return { durable: false };
  }
}

function isLegacyRecord(
  value: unknown,
): value is Omit<CheckoffRetirementRecordV2, "schemaVersion"> {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    Object.keys(v).length === 3 &&
    validId(v.groceryListId) &&
    validId(v.itemId) &&
    typeof v.supersededThroughOperationId === "string" &&
    parseCheckoffOperationId(v.supersededThroughOperationId) !== undefined
  );
}

export function mergeCheckoffRetirementStorageEvent(
  key: string | null,
  newValue: string | null,
): void {
  if (mergeCheckoffRetirementV3StorageEvent(key, newValue)) return;
  const record = parseRetirementStorageRecord(key, newValue);
  if (record) {
    merge(record);
    notifyRetirementSubscribers();
  }
}
export function isCheckoffOperationRetired(
  storage: RetirementStorage,
  entry: CheckoffOperationLike & { groceryListId: string; itemId: string },
): boolean {
  const boundary = retirementBoundaryFor(
    storage,
    entry.groceryListId,
    entry.itemId,
  );
  return (
    boundary !== undefined && !isCheckoffOperationNewerThan(entry, boundary)
  );
}
export function subscribeToCheckoffRetirements(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function resetCheckoffRetirementsForTests(): void {
  maxima.clear();
  v3Observed.clear();
  listeners.clear();
}
